import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Divider,
  Box,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../../utils/api";
import { useAuth } from "../../context/AuthContext";

const positions = [
  "QB",
  "RB",
  "WR",
  "TE",
  "OL",
  "DL",
  "LB",
  "CB",
  "S",
  "K",
  "P",
  "H",
  "LS",
];

const graduationYears = [2023, 2024, 2025, 2026];

const AddProspect = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    position: "",
    highSchool: "",
    graduationYear: "",
    height: "",
    heightFeet: "",
    heightInches: "",
    weight: "",
    wingspan: "",
    standingReach: "",
    dateOfBirth: "",
    fortyYardDash: "",
    verticalJump: "",
    benchPress: "",
    shuttleRun: "",
    phone: "",
    email: "",
    address: {
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: "USA",
    },
    academics: {
      gpa: "",
      transcripts: [],
    },
    social: {
      twitter: "",
      instagram: "",
      hudl: [""],
      youtube: [""],
    },
    verification: {},
    evaluations: [],
    injuryHistory: [],
    highlights: [],
    recruitingStatus: "prospect",
    notes: "", // Will be converted to array format for API
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("address.")) {
      const addressField = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value,
        },
      }));
    } else if (name.startsWith("academics.")) {
      const academicField = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        academics: {
          ...prev.academics,
          [academicField]: value,
        },
      }));
    } else if (name.startsWith("social.")) {
      const socialField = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        social: {
          ...prev.social,
          [socialField]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Auto-update combined height when feet or inches change
    if (name === "heightFeet" || name === "heightInches") {
      const feet = name === "heightFeet" ? value : formData.heightFeet;
      const inches = name === "heightInches" ? value : formData.heightInches;

      if (feet || inches) {
        const totalInches =
          (parseInt(feet) || 0) * 12 + (parseInt(inches) || 0);
        setFormData((prev) => ({
          ...prev,
          height: totalInches.toString(),
        }));
      }
    }
  };

  // Handle adding a journal entry
  const handleAddJournalEntry = (type) => {
    const newEntry = {
      text: "",
      date: new Date().toISOString(),
      author: user?.name || "Coach",
    };

    setFormData((prev) => ({
      ...prev,
      [type]: [...prev[type], newEntry],
    }));
  };

  // Handle changing a journal entry
  const handleJournalEntryChange = (type, index, field, value) => {
    setFormData((prev) => {
      const updatedEntries = [...prev[type]];
      updatedEntries[index] = {
        ...updatedEntries[index],
        [field]: value,
      };
      return {
        ...prev,
        [type]: updatedEntries,
      };
    });
  };

  // Handle removing a journal entry
  const handleRemoveJournalEntry = (type, index) => {
    setFormData((prev) => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index),
    }));
  };

  // Handle adding a social media link
  const handleAddSocialLink = (platform) => {
    setFormData((prev) => ({
      ...prev,
      social: {
        ...prev.social,
        [platform]: [...prev.social[platform], ""],
      },
    }));
  };

  // Handle changing a social media link
  const handleSocialLinkChange = (platform, index, value) => {
    setFormData((prev) => {
      const updatedLinks = [...prev.social[platform]];
      updatedLinks[index] = value;
      return {
        ...prev,
        social: {
          ...prev.social,
          [platform]: updatedLinks,
        },
      };
    });
  };

  // Handle removing a social media link
  const handleRemoveSocialLink = (platform, index) => {
    setFormData((prev) => ({
      ...prev,
      social: {
        ...prev.social,
        [platform]: prev.social[platform].filter((_, i) => i !== index),
      },
    }));
  };

  // Handle adding a transcript
  const handleAddTranscript = () => {
    setFormData((prev) => ({
      ...prev,
      academics: {
        ...prev.academics,
        transcripts: [...prev.academics.transcripts, { name: "", url: "" }],
      },
    }));
  };

  // Handle changing a transcript
  const handleTranscriptChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedTranscripts = [...prev.academics.transcripts];
      updatedTranscripts[index] = {
        ...updatedTranscripts[index],
        [field]: value,
      };
      return {
        ...prev,
        academics: {
          ...prev.academics,
          transcripts: updatedTranscripts,
        },
      };
    });
  };

  // Handle removing a transcript
  const handleRemoveTranscript = (index) => {
    setFormData((prev) => ({
      ...prev,
      academics: {
        ...prev.academics,
        transcripts: prev.academics.transcripts.filter((_, i) => i !== index),
      },
    }));
  };

  // Handle adding a highlight video
  const handleAddHighlight = () => {
    setFormData((prev) => ({
      ...prev,
      highlights: [...prev.highlights, { title: "", url: "" }],
    }));
  };

  // Handle changing a highlight video
  const handleHighlightChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedHighlights = [...prev.highlights];
      updatedHighlights[index] = {
        ...updatedHighlights[index],
        [field]: value,
      };
      return {
        ...prev,
        highlights: updatedHighlights,
      };
    });
  };

  // Handle removing a highlight video
  const handleRemoveHighlight = (index) => {
    setFormData((prev) => ({
      ...prev,
      highlights: prev.highlights.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Prepare data for API
      const prospectData = {
        ...formData,
        // Convert numeric fields
        weight: formData.weight ? parseInt(formData.weight) : undefined,
        wingspan: formData.wingspan ? parseInt(formData.wingspan) : undefined,
        standingReach: formData.standingReach
          ? parseInt(formData.standingReach)
          : undefined,
        benchPress: formData.benchPress
          ? parseInt(formData.benchPress)
          : undefined,
        verticalJump: formData.verticalJump
          ? parseFloat(formData.verticalJump)
          : undefined,
        // Speed data - nested under speedData object
        speedData: (() => {
          const speedData = {};
          if (formData.fortyYardDash) {
            speedData.fortyYard = parseFloat(formData.fortyYardDash);
          }
          if (formData.shuttleRun) {
            speedData.shuttleRun = parseFloat(formData.shuttleRun);
          }
          return Object.keys(speedData).length > 0 ? speedData : undefined;
        })(),
        graduationYear: formData.graduationYear
          ? parseInt(formData.graduationYear)
          : undefined,
        academics: (() => {
          const academics = {};
          if (formData.academics.gpa) {
            academics.gpa = parseFloat(formData.academics.gpa);
          }
          const transcripts = formData.academics.transcripts.filter(
            (t) => t && t.name && t.url
          );
          if (transcripts.length > 0) {
            academics.transcripts = transcripts;
          }
          return Object.keys(academics).length > 0 ? academics : undefined;
        })(),
        // Social media - nested under socialMedia object
        socialMedia: (() => {
          const socialMedia = {};
          if (formData.social.twitter) {
            socialMedia.twitter = formData.social.twitter;
          }
          if (formData.social.instagram) {
            socialMedia.instagram = formData.social.instagram;
          }
          const hudlLinks = formData.social.hudl.filter((link) => link);
          if (hudlLinks.length > 0) {
            socialMedia.hudl = hudlLinks;
          }
          const youtubeLinks = formData.social.youtube.filter((link) => link);
          if (youtubeLinks.length > 0) {
            socialMedia.youtube = youtubeLinks;
          }
          return Object.keys(socialMedia).length > 0 ? socialMedia : undefined;
        })(),
        // Journal entries
        evaluations: formData.evaluations.filter((entry) => entry.text.trim()),
        injuryHistory: formData.injuryHistory.filter((entry) =>
          entry.text.trim()
        ),
        // Highlight videos
        highlights: formData.highlights.filter((highlight) =>
          highlight.url.trim()
        ),
        // Verification status
        verification:
          Object.keys(formData.verification).length > 0
            ? formData.verification
            : undefined,
        // Convert notes string to array format expected by backend
        notes: formData.notes
          ? [
              {
                text: formData.notes,
                category: "general",
                createdBy: user?.id || user?._id,
              },
            ]
          : [],
        createdBy: user?.id || user?._id,
      };

      // Remove the separate height fields from the API data (keep only the combined height)
      delete prospectData.heightFeet;
      delete prospectData.heightInches;

      // Remove the old speed field names since they're now in speedData
      delete prospectData.fortyYardDash;
      delete prospectData.shuttleRun;

      // Remove empty fields
      Object.keys(prospectData).forEach((key) => {
        if (prospectData[key] === "" || prospectData[key] === undefined) {
          delete prospectData[key];
        }
      });

      // Remove empty address fields but keep the object if any fields are filled
      if (prospectData.address) {
        Object.keys(prospectData.address).forEach((key) => {
          if (prospectData.address[key] === "") {
            delete prospectData.address[key];
          }
        });
        if (Object.keys(prospectData.address).length === 0) {
          delete prospectData.address;
        }
      }

      // Remove empty academics (now handled in the academics object creation above)

      // Remove empty social media (now handled in the socialMedia object creation above)
      await api.prospects.create(prospectData);

      setSuccess(true);
      setTimeout(() => {
        navigate("/prospects");
      }, 2000);
    } catch (err) {
      console.error("Error creating prospect:", err);
      setError(err.message || "Failed to add prospect. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/prospects")}
        >
          Back to Prospects
        </Button>
      </Box>

      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Add New Prospect
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                select
                label="Position"
                name="position"
                value={formData.position}
                onChange={handleChange}
              >
                {positions.map((pos) => (
                  <MenuItem key={pos} value={pos}>
                    {pos}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Graduation Year"
                name="graduationYear"
                value={formData.graduationYear}
                onChange={handleChange}
              >
                {graduationYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {/* School & Contact */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                School & Contact Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="High School"
                name="highSchool"
                value={formData.highSchool}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Recruiting Status"
                name="recruitingStatus"
                value={formData.recruitingStatus}
                onChange={handleChange}
              >
                <MenuItem value="prospect">Prospect</MenuItem>
                <MenuItem value="contacted">Contacted</MenuItem>
                <MenuItem value="interested">Interested</MenuItem>
                <MenuItem value="offered">Offered</MenuItem>
                <MenuItem value="committed">Committed</MenuItem>
              </TextField>
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Address
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                name="address.street"
                value={formData.address.street}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                name="address.city"
                value={formData.address.city}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State"
                name="address.state"
                value={formData.address.state}
                onChange={handleChange}
                placeholder="e.g., CA, TX, FL"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="ZIP Code"
                name="address.zipCode"
                value={formData.address.zipCode}
                onChange={handleChange}
              />
            </Grid>

            {/* Physical Stats */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Physical Measurements
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Height (Feet)"
                name="heightFeet"
                type="number"
                inputProps={{ min: 4, max: 8 }}
                value={formData.heightFeet}
                onChange={handleChange}
                placeholder="e.g., 6"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Height (Inches)"
                name="heightInches"
                type="number"
                inputProps={{ min: 0, max: 11 }}
                value={formData.heightInches}
                onChange={handleChange}
                placeholder="e.g., 2"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Weight (lbs)"
                name="weight"
                type="number"
                value={formData.weight}
                onChange={handleChange}
                placeholder="e.g., 185"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Wingspan (in)"
                name="wingspan"
                type="number"
                value={formData.wingspan}
                onChange={handleChange}
                placeholder="e.g., 78"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Standing Reach (in)"
                name="standingReach"
                type="number"
                value={formData.standingReach}
                onChange={handleChange}
                placeholder="e.g., 96"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                name="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Performance Metrics */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Performance Metrics
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="40-Yard Dash (s)"
                name="fortyYardDash"
                type="number"
                inputProps={{ step: 0.01 }}
                value={formData.fortyYardDash}
                onChange={handleChange}
                placeholder="e.g., 4.5"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Vertical Jump (in)"
                name="verticalJump"
                type="number"
                inputProps={{ step: 0.5 }}
                value={formData.verticalJump}
                onChange={handleChange}
                placeholder="e.g., 36"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bench Press (lbs)"
                name="benchPress"
                type="number"
                value={formData.benchPress}
                onChange={handleChange}
                placeholder="e.g., 225"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Shuttle Run (s)"
                name="shuttleRun"
                type="number"
                inputProps={{ step: 0.01 }}
                value={formData.shuttleRun}
                onChange={handleChange}
                placeholder="e.g., 4.2"
              />
            </Grid>

            {/* Social Media */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Social Media
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Twitter"
                name="social.twitter"
                value={formData.social.twitter}
                onChange={handleChange}
                placeholder="@username"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Instagram"
                name="social.instagram"
                value={formData.social.instagram}
                onChange={handleChange}
                placeholder="@username"
              />
            </Grid>

            {/* Hudl Links */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Hudl Links
              </Typography>
              {formData.social.hudl.map((link, index) => (
                <Box key={index} sx={{ display: "flex", mb: 1 }}>
                  <TextField
                    fullWidth
                    label={`Hudl Link ${index + 1}`}
                    value={link || ""}
                    onChange={(e) =>
                      handleSocialLinkChange("hudl", index, e.target.value)
                    }
                    placeholder="https://www.hudl.com/profile/..."
                    sx={{ mr: 1 }}
                  />
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveSocialLink("hudl", index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={() => handleAddSocialLink("hudl")}
                sx={{ mt: 1 }}
              >
                Add Hudl Link
              </Button>
            </Grid>

            {/* YouTube Links */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                YouTube Links
              </Typography>
              {formData.social.youtube.map((link, index) => (
                <Box key={index} sx={{ display: "flex", mb: 1 }}>
                  <TextField
                    fullWidth
                    label={`YouTube Link ${index + 1}`}
                    value={link || ""}
                    onChange={(e) =>
                      handleSocialLinkChange("youtube", index, e.target.value)
                    }
                    placeholder="https://www.youtube.com/watch?v=..."
                    sx={{ mr: 1 }}
                  />
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveSocialLink("youtube", index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={() => handleAddSocialLink("youtube")}
                sx={{ mt: 1 }}
              >
                Add YouTube Link
              </Button>
            </Grid>

            {/* Academics */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Academic Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="GPA"
                name="academics.gpa"
                type="number"
                inputProps={{ min: 0, max: 4.0, step: 0.1 }}
                value={formData.academics.gpa}
                onChange={handleChange}
              />
            </Grid>

            {/* Academic Transcripts */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                Academic Transcripts
              </Typography>
              {formData.academics.transcripts.map((transcript, index) => (
                <Box key={index} sx={{ display: "flex", mb: 1 }}>
                  <TextField
                    sx={{ mr: 1, flexGrow: 1 }}
                    label={`Transcript Name`}
                    value={transcript.name || ""}
                    onChange={(e) =>
                      handleTranscriptChange(index, "name", e.target.value)
                    }
                    placeholder="e.g. Fall 2024 Semester"
                  />
                  <TextField
                    sx={{ mr: 1, flexGrow: 2 }}
                    label={`URL or File Location`}
                    value={transcript.url || ""}
                    onChange={(e) =>
                      handleTranscriptChange(index, "url", e.target.value)
                    }
                    placeholder="https://example.com/transcript.pdf"
                  />
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveTranscript(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={handleAddTranscript}
                sx={{ mt: 1 }}
              >
                Add Transcript
              </Button>
            </Grid>

            {/* Highlights */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Highlight Videos
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {formData.highlights.map((highlight, index) => (
                <Box key={index} sx={{ display: "flex", mb: 1 }}>
                  <TextField
                    sx={{ mr: 1, flexGrow: 1 }}
                    label={`Title`}
                    value={highlight.title || ""}
                    onChange={(e) =>
                      handleHighlightChange(index, "title", e.target.value)
                    }
                    placeholder="e.g. Senior Year Highlights"
                  />
                  <TextField
                    sx={{ mr: 1, flexGrow: 2 }}
                    label={`Video URL`}
                    value={highlight.url || ""}
                    onChange={(e) =>
                      handleHighlightChange(index, "url", e.target.value)
                    }
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveHighlight(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={handleAddHighlight}
                sx={{ mt: 1 }}
              >
                Add Highlight Video
              </Button>
            </Grid>

            {/* Evaluations */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Evaluations
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {formData.evaluations.map((entry, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      {entry.author} •{" "}
                      {new Date(entry.date).toLocaleDateString()}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() =>
                        handleRemoveJournalEntry("evaluations", index)
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={entry.text}
                    onChange={(e) =>
                      handleJournalEntryChange(
                        "evaluations",
                        index,
                        "text",
                        e.target.value
                      )
                    }
                    placeholder="Enter evaluation notes..."
                  />
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={() => handleAddJournalEntry("evaluations")}
                sx={{ mt: 1 }}
              >
                Add Evaluation
              </Button>
            </Grid>

            {/* Injury History */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Injury History
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {formData.injuryHistory.map((entry, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      {entry.author} •{" "}
                      {new Date(entry.date).toLocaleDateString()}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() =>
                        handleRemoveJournalEntry("injuryHistory", index)
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={entry.text}
                    onChange={(e) =>
                      handleJournalEntryChange(
                        "injuryHistory",
                        index,
                        "text",
                        e.target.value
                      )
                    }
                    placeholder="Enter injury details..."
                  />
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={() => handleAddJournalEntry("injuryHistory")}
                sx={{ mt: 1 }}
              >
                Add Injury Record
              </Button>
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Notes
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Scouting Notes"
                name="notes"
                multiline
                rows={4}
                value={formData.notes}
                onChange={handleChange}
                placeholder="Add any scouting notes or additional information..."
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={loading}
              sx={{ minWidth: 120 }}
            >
              {loading ? "Saving..." : "Save Prospect"}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/prospects")}
              disabled={loading}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Success Snackbar */}
      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccess(false)}>
          Prospect added successfully! Redirecting...
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddProspect;
